import { Injectable } from '@angular/core';
import { webDtos } from '@vectronic/openApi-customer';
import simplify from 'simplify-js';
import { L } from '../../services/leaflet-setup/leaflet-setup';

export interface Position {
  latlng: L.LatLng;
  timestamp: number;
}

interface TimedPoint {
  x: number;
  y: number;
  timestamp: number;
}

@Injectable()
export class TrackplayCalculationService {
  simplifyPositions(positions: Position[], tolerance: number): Position[] {
    const pointsForSimplification: TimedPoint[] = positions.map((pos) => ({
      x: pos.latlng.lng,
      y: pos.latlng.lat,
      timestamp: pos.timestamp,
    }));

    const simplifiedPoints = simplify(pointsForSimplification, tolerance, false) as TimedPoint[];
    return simplifiedPoints.map((point) => ({
      latlng: L.latLng(point.y, point.x),
      timestamp: point.timestamp,
    }));
  }

  formattedPositions(collar: webDtos.MapPositionsList): Position[] {
    const positions = collar.positions
      .filter((pos) => {
        const date = new Date(pos.acquisitionTime);
        return pos.latitude && pos.longitude && pos.acquisitionTime && !isNaN(date.getTime());
      })
      .map((pos) => ({
        latlng: L.latLng(pos.latitude, pos.longitude),
        timestamp: new Date(pos.acquisitionTime).getTime(),
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    return positions;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  calculateStepSize(ceil: number, floor: number, speedValue: number): number {
    const durationMs = ceil - floor;
    let minSteps: number;
    const durationDays = durationMs / (24 * 60 * 60 * 1000);
    if (durationDays < 4) {
      minSteps = 200;
    } else if (durationDays < 28) {
      minSteps = 800;
    } else if (durationDays < 120) {
      minSteps = 1600;
    } else if (durationDays < 365) {
      minSteps = 2000;
    } else if (durationDays < 3 * 365) {
      minSteps = 2400;
    } else {
      minSteps = 2800;
    }
    const maxSteps = 500;
    const minStepSize = durationMs / maxSteps;
    const maxStepSize = durationMs / minSteps;

    const timeIncrements = [
      { ms: 1 * 60 * 1000, maxDurationMs: 6 * 60 * 60 * 1000 },
      { ms: 5 * 60 * 1000, maxDurationMs: 12 * 60 * 60 * 1000 },
      { ms: 15 * 60 * 1000, maxDurationMs: 1 * 24 * 60 * 60 * 1000 },
      { ms: 1 * 60 * 60 * 1000, maxDurationMs: 7 * 24 * 60 * 60 * 1000 },
      { ms: 6 * 60 * 60 * 1000, maxDurationMs: 30 * 24 * 60 * 60 * 1000 },
      { ms: 1 * 24 * 60 * 60 * 1000, maxDurationMs: 365 * 24 * 60 * 60 * 1000 },
      { ms: 7 * 24 * 60 * 60 * 1000, maxDurationMs: 3 * 365 * 24 * 60 * 60 * 1000 },
      { ms: 30 * 24 * 60 * 60 * 1000, maxDurationMs: Infinity },
    ];

    let speedSteps: number;
    if (speedValue <= 300) {
      speedSteps = 150;
    } else if (speedValue >= 604800) {
      speedSteps = 250;
    } else {
      speedSteps = 200;
    }
    const targetSteps = Math.min(maxSteps, Math.max(minSteps, speedSteps));
    const idealStepSize = durationMs / targetSteps;

    const speedScalingFactor = Math.max(1, Math.log10(speedValue / 60));
    let stepSize = timeIncrements[0].ms;

    for (const increment of timeIncrements) {
      const adjustedIncrementMs = increment.ms * speedScalingFactor;
      if (idealStepSize <= adjustedIncrementMs && durationMs <= increment.maxDurationMs) {
        stepSize = adjustedIncrementMs;
        break;
      }
      stepSize = adjustedIncrementMs;
    }

    stepSize = Math.max(minStepSize, Math.min(stepSize, maxStepSize));

    if (durationMs < 1 * 60 * 1000) {
      stepSize = Math.max(durationMs / maxSteps, 1000);
    }

    stepSize = Math.round(stepSize / 1000) * 1000;

    const numSteps = durationMs / stepSize;
    if (numSteps < minSteps) {
      stepSize = durationMs / minSteps;
    } else if (numSteps > maxSteps) {
      stepSize = durationMs / maxSteps;
    }

    if (isNaN(stepSize) || stepSize <= 0) {
      stepSize = 1000;
    }

    const finalNumSteps = durationMs / stepSize;
    console.log(
      `Duration: ${durationMs}ms (${Math.round(durationMs / (24 * 60 * 60 * 1000))} days), ` +
        `Speed: ${speedValue}, Step size: ${stepSize}ms (${Math.round(stepSize / (60 * 1000))} minutes), ` +
        `Steps: ${Math.round(finalNumSteps)}`,
    );

    return stepSize;
  }

  calculateTrackGaps(
    fullTrackPositions: { positions: Position[] }[],
    stepSize: number,
  ): { start: number; end: number }[] {
    const GAP_THRESHOLD = Math.max(stepSize, 60000);
    const gaps: { start: number; end: number }[] = [];

    fullTrackPositions.forEach((track) => {
      const positions = track.positions;

      for (let i = 0; i < positions.length - 1; i++) {
        const currentTime = positions[i].timestamp;
        const nextTime = positions[i + 1].timestamp;
        const timeDiff = nextTime - currentTime;

        if (timeDiff > GAP_THRESHOLD) {
          gaps.push({
            start: currentTime,
            end: nextTime,
          });
        }
      }
    });

    gaps.sort((a, b) => a.start - b.start);

    const mergedGaps: { start: number; end: number }[] = [];
    for (const gap of gaps) {
      if (mergedGaps.length === 0 || mergedGaps[mergedGaps.length - 1].end < gap.start) {
        mergedGaps.push(gap);
      } else {
        mergedGaps[mergedGaps.length - 1].end = Math.max(
          mergedGaps[mergedGaps.length - 1].end,
          gap.end,
        );
      }
    }

    return mergedGaps;
  }

  calculateCentroid(activeSegments: Map<string, L.LatLng[]>): L.LatLng {
    let latSum = 0;
    let lngSum = 0;
    let count = 0;
    activeSegments.forEach((latLngs) => {
      latLngs.forEach((point) => {
        latSum += point.lat;
        lngSum += point.lng;
        count++;
      });
    });
    const centroidLat = latSum / count;
    const centroidLng = lngSum / count;
    return L.latLng(centroidLat, centroidLng);
  }

  smoothTargetLocation(
    targets: L.LatLng[],
    lastPanLocation: L.LatLng | null,
    defaultCenter: L.LatLng,
  ): L.LatLng {
    if (targets.length === 0) return lastPanLocation || defaultCenter;

    let latSum = 0;
    let lngSum = 0;
    let weightSum = 0;
    targets.forEach((t, i) => {
      const weight = (i + 1) / targets.length;
      latSum += t.lat * weight;
      lngSum += t.lng * weight;
      weightSum += weight;
    });
    return L.latLng(latSum / weightSum, lngSum / weightSum);
  }

  getDistanceInPixels(current: L.LatLng, target: L.LatLng, map: L.Map): number {
    const currentPoint = map.latLngToContainerPoint(current);
    const targetPoint = map.latLngToContainerPoint(target);
    return currentPoint.distanceTo(targetPoint);
  }

  calculateBounds(
    fullTrackPositions: { id: number; positions: Position[]; color: string }[],
  ): L.LatLngBounds {
    const latLngs: L.LatLng[] = [];

    fullTrackPositions.forEach((track) => {
      track.positions.forEach((position) => {
        latLngs.push(position.latlng);
      });
    });

    if (latLngs.length === 0) {
      return L.latLngBounds([]);
    }

    return L.latLngBounds(latLngs);
  }
}
